import i18n from "@/lang";
import { LogService } from "@/services/logging";
import { createGzip } from "zlib";
import { ElasticsearchAccessError } from "@/constants/errors/elasticsearch";

// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;

export enum QueryParameters {
  groupId = "gid",
}

export function getParameterByName(
  name: string,
  url: string = window.location.href
): string | null {
  // eslint-disable-next-line no-useless-escape
  name = name.replace(/[\[\]]/g, "\\$&");

  const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)");

  const results = regex.exec(url);

  if (!results) {
    return null;
  }
  if (!results[2]) {
    return "";
  }
  return decodeURIComponent(results[2].replace(/\+/g, " "));
}

export function sumArray(array: number[]) {
  let sum = 0;
  array.forEach((item) => (sum = sum + item));
  return sum;
}

/**
 * Returns a random number between two integers
 * min and max included
 */
export function randomIntFromInterval(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

/**
 * Takes an error object, and return an object with the type of the error and a message to be shown for the user
 * For "Business" errors needs to be improved once used (e.g: promocode not valid - customer already exist ... etc)
 */
export function handleErrorAndPrepareMessage(error: any): {
  errorMessage: string;
  errorType: "Network" | "ServerError" | "Graphql" | "Business" | "";
  originalMessage: string;
} {
  // Fall back to a connectivity message when the browser reports it is offline,
  const isOffline = typeof navigator !== "undefined" && !navigator.onLine;
  let errorMessage = isOffline
    ? t("error.no-internet")
    : t("error.general-error");
  let errorType: "Network" | "ServerError" | "Graphql" | "Business" | "" =
    isOffline ? "Network" : "";

  if (error instanceof ElasticsearchAccessError) {
    // Elasticsearch permission issue (missing token or 401/403)
    errorMessage = t("error.insufficient-permissions");
    errorType = "ServerError";
  } else if (error.networkError) {
    const statusCode = error.networkError.statusCode;
    if (statusCode === 401 || statusCode === 403) {
      // Auth failure.
      // Map to the same message as axios 401/403 so callers show a consistent auth message.
      errorMessage = t("error.insufficient-permissions");
      errorType = "ServerError";
    } else if (statusCode) {
      // Server responded with a non-2xx status (e.g. 500)
      // The request reached the server but the server returned an error
      errorMessage = t("error.server-error");
      errorType = "ServerError";
    } else {
      // True network failure, request never reached the server
      errorMessage = t("error.no-internet");
      errorType = "Network";
    }
  } else if (error.graphQLErrors && error.graphQLErrors.length) {
    // Request reached the server but GraphQL returned errors in the response
    errorMessage = t("error.general-error");
    errorType = "Graphql";
  } else if (error.response) {
    const status = error.response.status;
    // Detect 401/403 from any HTTP client (including Elasticsearch axios calls)
    if (status === 401 || status === 403) {
      errorMessage = t("error.insufficient-permissions");
      errorType = "ServerError";
    } else {
      // REST API error: the request reached server and we have a response prepared by backend
      errorType = "Business";
      const data = error && error.response && error.response.data;
      const msg = data.messages && data.messages.length ? data.messages[0] : "";

      // Error message shown to the user will depend on msg value
      // Here we should put all error messages that we can get from backend
      // For now only POST customer api error response are considered
      switch (msg) {
        // 1- POST Customer errors
        case "Customer already exists.":
          errorMessage = t("error.customer-already-exists");
          break;
        case "email: This value should not be null.":
          errorMessage = t("error.email-null");
          break;
        case "firstName: This value should not be null.":
          errorMessage = t("error.firstname-null");
          break;
        case "lastName: This value should not be null.":
          errorMessage = t("error.lastname-null");
          break;
        // 2- POST pos/email errors
      }
    }
  }

  return { errorMessage, errorType, originalMessage: error.message };
}

export function areObjectsEqual(
  obj1: Record<string, any>,
  obj2: Record<string, any>
) {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) {
    return false;
  }
  for (const key of keys1) {
    if (obj1[key] !== obj2[key]) {
      return false;
    }
  }
  return true;
}

export function roundToNearestMultipleOf5(value: number) {
  const remainder = value % 5;
  // If remainder is less than 3, round down to the nearest multiple of 5
  // Otherwise, round up to the nearest multiple of 5
  if (remainder < 3) {
    return value - remainder;
  } else {
    return value + (5 - remainder);
  }
}

export function isMultipleOf5(value: number) {
  return value % 5 === 0;
}

/**
 * Guards against floating point drift when money is converted between cents and
 * currency units.
 */
export function roundToTwoDecimals(value: number) {
  return Number(value.toFixed(2));
}

export function createAsyncLockedFunction(
  func: (...args: any[]) => Promise<any>
) {
  let isCalled = false;

  return async function (...args: any[]) {
    if (!isCalled) {
      isCalled = true;
      try {
        const result = await func(...args);
        return result;
      } finally {
        isCalled = false; // Reset the flag after the original function finishes
      }
    } else {
      console.log("This function has already been called and is now locked.", {
        function: func,
      });
      LogService.info("User tried to use a locked function twice", {
        function: func,
      });
    }
  };
}

export function formatMemoryUsage(data: number) {
  return `${Math.round((data / 1024 / 1024) * 100) / 100} MB`;
}

/**
 * Calculates the total CPU usage from the result of os.cpus() in Node.js.
 * @param cpus - Array containing CPU usage data for each CPU core.
 * @returns The percentage of CPU usage.
 */
export function calculateCpuUsage(cpus: any[]) {
  // Sum up the CPU times for user, nice, sys, and idle for all CPU cores.
  const totalCpuUsage = cpus.reduce((total: number, cpu: any) => {
    return (
      total + cpu.times.user + cpu.times.nice + cpu.times.sys + cpu.times.idle
    );
  }, 0);

  // Calculate the average CPU usage across all CPU cores.
  const averageCpuUsage = totalCpuUsage / cpus.length;

  // Calculate the CPU usage percentage based on the first CPU core's idle time.
  const cpuUsagePercentage = 100 - 100 * (cpus[0].times.idle / averageCpuUsage);

  return cpuUsagePercentage.toFixed(2);
}

/**
 *
 * @returns BO base url for the environment
 */
export function getBackOfficeBaseUrl() {
  const env = import.meta.env.VITE_ENV;
  return env === "dev" || env === "test"
    ? `https://${env}-app.smeetz.com`
    : "https://app.smeetz.com";
}

/**
 * Gzip compress a JSON object
 * @param jsonObject The JSON object to compress
 * @returns A Promise that resolves to a Buffer containing the compressed data
 */
export function gzipCompressJson(jsonObject: any): Promise<Buffer> {
  return new Promise((resolve, reject) => {
    const jsonString = JSON.stringify(jsonObject);
    const gzip = createGzip();
    const chunks: Buffer[] = [];

    gzip.on("data", (chunk) => chunks.push(chunk));
    gzip.on("end", () => resolve(Buffer.concat(chunks)));
    gzip.on("error", reject);

    gzip.end(jsonString); // Write JSON string to the gzip stream
  });
}

/**
 * Converts a number (e.g., in euros) to cents by rounding to the nearest whole number.
 * Example: 10.235 => 1024 (cents)
 */
export function convertToCents(amount: number) {
  // Multiply by 100 to shift two decimal places, then round to nearest integer
  return Math.round(amount * 100);
}

export function openLink(url: string) {
  // open link in new window
  window.open(url, "_blank", "noopener,noreferrer");
}
